import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { confirmDelete } from '../utils/confirmDelete';
import { T, PageHeader, Alert } from '../components/UI';

const PAGE_SIZE = 10;

export default function Services() {
  const navigate = useNavigate();
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error,    setError]    = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({ name:'' });
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [search,   setSearch]   = useState('');
  const [page,     setPage]     = useState(1);

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/jobs/services');
      setItems((data.services||[]).slice().sort((a,b)=>a.name.localeCompare(b.name)));
    } catch { setError('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true); setError(null);
    try {
      if (editing) { await api.patch(`/jobs/services/${editing._id}`, form); }
      else { await api.post('/jobs/services', form); }
      setForm({ name:'' }); setShowForm(false); setEditing(null); fetch();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const startEdit = (item) => {
    setEditing(item); setForm({ name:item.name||'' });
    setShowForm(true); window.scrollTo({top:0,behavior:'smooth'});
  };
  const cancelForm = () => { setShowForm(false); setEditing(null); setForm({ name:'' }); };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"?`)) return;
    if (!await confirmDelete()) return;
    setDeleting(id);
    try { await api.delete(`/jobs/services/${id}`); fetch(); }
    catch { setError('Failed to delete'); }
    finally { setDeleting(null); }
  };

  // Filter + paginate
  const q = search.trim().toLowerCase();
  const filtered = q ? items.filter(i=>i.name.toLowerCase().includes(q)) : items;
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  // Reset page on search
  const handleSearch = (val) => { setSearch(val); setPage(1); };

  return (
    <div style={T.page}>
      <PageHeader title="Services" onBack={()=>navigate(-1)} actions={
        <button onClick={()=>{ cancelForm(); setShowForm(s=>!s); }} style={T.btnPrimary}>
          <i className="ti ti-plus" aria-hidden="true"/> Add
        </button>
      }/>
      <div style={{ maxWidth:'600px', margin:'0 auto', padding:'16px 16px 80px' }}>
        {error && <Alert type="error">{error}</Alert>}

        {/* Add/Edit form */}
        {showForm && (
          <div style={{ background:'var(--surface)', border:`1px solid ${editing?'var(--amber)':'var(--blue)'}`, borderRadius:'var(--radius-lg)', padding:'16px', marginBottom:'16px' }}>
            <div style={{ fontSize:'13px', fontWeight:'500', color:editing?'var(--amber)':'var(--blue)', marginBottom:'12px' }}>
              {editing ? `Edit: ${editing.name}` : 'Add New Service'}
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom:'12px' }}>
                <label style={T.label}>Service Name *</label>
                <input value={form.name} onChange={e=>setForm({name:e.target.value})}
                  style={T.input} placeholder="e.g. Full Detail" required autoFocus
                  onFocus={e=>e.target.style.borderColor='var(--blue)'}
                  onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
              </div>
              <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
                <button type="button" onClick={cancelForm} style={T.btnSecondary}>Cancel</button>
                <button type="submit" disabled={saving} style={{ ...T.btnPrimary, background:editing?'var(--amber)':'var(--blue)', opacity:saving?0.6:1 }}>
                  {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Service'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search bar */}
        <div style={{ position:'relative', marginBottom:'12px' }}>
          <i className="ti ti-search" style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', color:'var(--hint)', fontSize:'15px', pointerEvents:'none' }} aria-hidden="true"/>
          <input value={search} onChange={e=>handleSearch(e.target.value)}
            placeholder="Search services..."
            style={{ ...T.input, paddingLeft:'32px' }}
            onFocus={e=>e.target.style.borderColor='var(--blue)'}
            onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
          {search && (
            <button onClick={()=>handleSearch('')} style={{ position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--hint)', display:'flex', alignItems:'center' }}>
              <i className="ti ti-x" style={{ fontSize:'14px' }} aria-label="Clear"/>
            </button>
          )}
        </div>

        {/* Count */}
        <div style={{ fontSize:'12px', color:'var(--muted)', marginBottom:'10px' }}>
          {filtered.length} service{filtered.length!==1?'s':''}{q?` matching "${search}"`:''}
        </div>

        {/* List */}
        {loading ? (
          <div style={{ textAlign:'center', padding:'40px', color:'var(--muted)' }}>Loading...</div>
        ) : (
          <>
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {paged.map(item=>(
                <div key={item._id} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'12px 16px', display:'flex', alignItems:'center', gap:'12px' }}>
                  <div style={{ width:'34px', height:'34px', background:'var(--blue-light)', borderRadius:'var(--radius)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <i className="ti ti-tool" style={{ color:'var(--blue)', fontSize:'15px' }} aria-hidden="true"/>
                  </div>
                  <div style={{ flex:1, fontWeight:'500', color:'var(--text)', fontSize:'13px' }}>{item.name}</div>
                  <div style={{ display:'flex', gap:'6px' }}>
                    <button onClick={()=>startEdit(item)} style={{ ...T.btnIcon, color:'var(--blue)', borderColor:'rgba(10,110,180,0.25)' }}>
                      <i className="ti ti-edit" style={{ fontSize:'14px' }} aria-label="Edit"/>
                    </button>
                    <button onClick={()=>handleDelete(item._id, item.name)} disabled={deleting===item._id} style={{ ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)' }}>
                      <i className="ti ti-trash" style={{ fontSize:'14px' }} aria-label="Delete"/>
                    </button>
                  </div>
                </div>
              ))}
              {paged.length===0 && (
                <div style={{ textAlign:'center', padding:'40px', color:'var(--muted)', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)' }}>
                  {q ? `No services matching "${search}"` : 'No services yet. Click Add to get started.'}
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'6px', marginTop:'14px' }}>
                <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
                  style={{ ...T.btnSecondary, padding:'6px 10px', opacity:page===1?0.4:1 }}>
                  <i className="ti ti-chevron-left" style={{ fontSize:'14px' }} aria-label="Previous"/>
                </button>
                {Array.from({length:totalPages},(_,i)=>i+1).map(p=>(
                  <button key={p} onClick={()=>setPage(p)}
                    style={{ ...T.btnSecondary, padding:'6px 12px', minWidth:'34px', background:p===page?'var(--blue)':'transparent', color:p===page?'#fff':'var(--muted)', borderColor:p===page?'var(--blue)':'var(--border-md)' }}>
                    {p}
                  </button>
                ))}
                <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
                  style={{ ...T.btnSecondary, padding:'6px 10px', opacity:page===totalPages?0.4:1 }}>
                  <i className="ti ti-chevron-right" style={{ fontSize:'14px' }} aria-label="Next"/>
                </button>
                <span style={{ fontSize:'12px', color:'var(--muted)', marginLeft:'4px' }}>
                  {(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE,filtered.length)} of {filtered.length}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
